import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Step,
  StepLabel,
  Stepper,
} from '@mui/material';
import React from 'react';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import { date, money, units as formatUnits } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { useToast } from '../../ui/feedback';
import { PageHeader, SectionCard, StatCard, StatGrid, StatusChip } from '../../ui/primitives';
import BankFilePreview from '../common/BankFilePreview';

const FLOW = ['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'PAID'];

export default function CapitalReturnDetailPage() {
  const { capitalReturnId } = useParams();
  const scope = useTenantScope();
  const { state, dispatch } = useRegistry();
  const toast = useToast();
  const capitalReturn = scope.capitalReturns.find((item) => item.id === capitalReturnId);
  if (!capitalReturn) return <Navigate to="/manager/capital-returns" replace />;
  const fund = scope.fundById.get(capitalReturn.fundId)!;
  const paid = capitalReturn.status === 'PAID';
  const taxBatchDone = state.taxBatches.some(
    (batch) =>
      batch.fundId === fund.id && batch.financialYear === 'FY2027' && batch.status === 'PUBLISHED',
  );
  const checklist = [
    { label: 'Loan repayment received and reconciled by the fund accountant', done: true },
    {
      label: 'Final income and capital return approved',
      done: ['APPROVED', 'PAID'].includes(capitalReturn.status),
    },
    { label: 'Payments released and units cancelled', done: paid },
    { label: 'Exit statements and notices sent to every holder', done: paid },
    {
      label: 'Held or returned payments resolved',
      done:
        paid &&
        !state.paymentExceptions.some(
          (item) => item.fundId === fund.id && item.status !== 'REISSUED',
        ),
    },
    { label: 'FY2027 tax statements issued for the trust', done: taxBatchDone },
    { label: 'Trust included in the FY2027 AIIR lodgement', done: false },
    { label: 'Bank account closed and register archived (7-year retention)', done: false },
  ];

  return (
    <>
      <PageHeader
        eyebrow={`${fund.name} · requested by ${capitalReturn.createdBy}${capitalReturn.approvedBy ? `, approved by ${capitalReturn.approvedBy}` : ''}`}
        title={
          <>
            {capitalReturn.kind === 'FINAL'
              ? 'Final capital return and wind-up'
              : `${capitalReturn.returnPct}% capital return`}{' '}
            <StatusChip status={capitalReturn.status} />
          </>
        }
        subtitle={capitalReturn.reason}
        actions={
          <>
            {capitalReturn.status === 'PENDING_APPROVAL' && (
              <Button component={RouterLink} to="/manager/approvals" variant="contained">
                Open approvals
              </Button>
            )}
            {capitalReturn.status === 'APPROVED' && (
              <Button
                variant="contained"
                onClick={() => {
                  dispatch({ type: 'CAPITAL_RETURN_PAY', id: capitalReturn.id });
                  toast(
                    capitalReturn.kind === 'FINAL'
                      ? 'Paid: units cancelled, trust wound up, exit statements published'
                      : 'Capital returned and advices published',
                  );
                }}
              >
                Release payments
              </Button>
            )}
          </>
        }
      />
      <Stepper activeStep={Math.max(0, FLOW.indexOf(capitalReturn.status)) + 1} sx={{ mb: 3 }}>
        {['Calculated', 'Submitted', 'Approved', 'Paid'].map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <StatGrid>
        <StatCard label="Holders" value={capitalReturn.lines.length} />
        <StatCard
          label="Capital returned"
          value={money(sumBy(capitalReturn.lines, (line) => line.amount))}
        />
        <StatCard
          label="Final income"
          value={money(sumBy(capitalReturn.lines, (line) => line.finalIncome))}
          hint={`${capitalReturn.finalIncomeCentsPerUnit} cents per unit`}
        />
        <StatCard label="Payment date" value={date(capitalReturn.paymentDate)} />
      </StatGrid>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', xl: '3fr 2fr' } }}>
        <SectionCard title="Payments" noPadding>
          <DataTable
            rows={capitalReturn.lines}
            rowKey={(row) => row.holderId}
            dense
            pageSize={12}
            columns={[
              {
                key: 'holder',
                label: 'Holder',
                render: (row) => scope.holderById.get(row.holderId)?.name,
              },
              {
                key: 'cancel',
                label: 'Units cancelled',
                align: 'right',
                render: (row) => formatUnits(row.unitsCancelled),
              },
              {
                key: 'capital',
                label: 'Capital',
                align: 'right',
                render: (row) => money(row.amount, true),
              },
              {
                key: 'income',
                label: 'Income',
                align: 'right',
                render: (row) => money(row.finalIncome, true),
              },
              {
                key: 'bank',
                label: 'Bank',
                render: (row) =>
                  scope.holderById.get(row.holderId)?.bank.verified ? (
                    'Verified'
                  ) : (
                    <StatusChip status="PENDING" label="Held" />
                  ),
              },
            ]}
          />
        </SectionCard>
        <Box sx={{ display: 'grid', gap: 2, alignContent: 'start' }}>
          {capitalReturn.kind === 'FINAL' && (
            <SectionCard title="Wind-up checklist" noPadding>
              <List dense disablePadding>
                {checklist.map((item) => (
                  <ListItem
                    key={item.label}
                    sx={{ px: 2.5, borderTop: '1px solid', borderColor: 'divider' }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {item.done ? (
                        <CheckCircleIcon fontSize="small" color="success" />
                      ) : (
                        <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: '0.85rem' }}
                    />
                  </ListItem>
                ))}
              </List>
            </SectionCard>
          )}
          <BankFilePreview
            fund={fund}
            description="CAP RETURN"
            paymentDate={capitalReturn.paymentDate}
            fileName={`${fund.code}_CAPITAL_RETURN_${capitalReturn.paymentDate}.aba`}
            payments={capitalReturn.lines.map((line) => ({
              holderId: line.holderId,
              amount: line.amount + line.finalIncome,
            }))}
          />
        </Box>
      </Box>
    </>
  );
}
